/*
 * Copyright (c) 2022 CalAmp Corp.  All Rights Reserved
 */
import groupBy from 'lodash.groupby';
import isEqual from 'lodash.isequal';
import sortby from 'lodash.sortby';
import {
  Entity,
  GenericObject,
  Link,
  Permission,
  PermissionResults,
  Permissions
} from '@ion/core/src/types/entities';
import {
  Operation
} from '@ion/core/src/types/general';

/**
 * Defines the feature and permissions toggle here.
 */
export enum toggleOptions {
  PERMISSIONS = 'PERMISSIONS',
  FEATURES = 'FEATURES'
};

/**
 * Define the path for the localisation.
 */
export const path = 'admin-roles:permissions';

/**
 * Defines the permissions list here.
 */
export enum PermissionsOptions {
  JOB = 'job',
  JOBS = 'jobs',
  TASK = 'task',
  CUSTOMER_FILE = 'customerfile',
  CALAMP_SELECT_FILE = 'calampselectfile'
};

export const defaultPermissions = [
  Entity.application,
  Entity.role,
  Entity.user
];

/**
 * Get Actions from the grouped permissions.
 */
export const getActions = (groupedPermissions: Permissions, existingPermissions: GenericObject) => {
  let actions             = [];
  const isPermissionExist = existingPermissions?.length > 0;
  groupedPermissions?.forEach(({permission}) => {
    const {action, _self} = permission;
    actions.push({title: action, link: _self, checked: false});
  });
  if (isPermissionExist) {
    actions = checkActions(actions, existingPermissions);
  }
  return actions;
};

/**
 * Provides the resources from the permissions list;
 */
export const permissionResources = (permissions: Permissions, existingPermissions: GenericObject) => {
  const groupedPermissions = groupBy(permissions, 'permission.resource');
  const resources          = {};
  const permissionList     = [];
  permissions?.forEach(({permission}) => {
    const {resource, actions} = permission;
    if (!resources[resource]) {
      resources[resource] = resource;
      permission.actions = getActions(groupedPermissions[resource], existingPermissions);
      permissionList.push(permission);
    }
  });
  return sortby(permissionList, 'resource');
};

/**
 * Filter actions to disable the permission actions if the action is not
 * exist in the list.
 */
export const filterAction = (permission: Permission, action: String): Permission | boolean => {
  const {actions, resource}     = permission || {};
  const isFilePermissionsAction = filePermissionsPermission.indexOf(resource) > -1;
  if (!isFilePermissionsAction && !isEqual(action, Operation.Assign)) {
    return actions.some(({title}) => title === action);
  }
  return isFilePermissionsAction;
};

/**
 * Provides the links of default permissions if exist in the provided permissions.
 */
export const getDefaultPermissions = (permissions: PermissionResults): Link[] => (
  permissions?.filter(({permission: {action, resource}}) => (
    action === 'read' &&
    (defaultPermissions.find(permission => isEqual(permission, resource))?.length > 0)
  ))?.map(({permission: {_self}}) => _self)
);

/**
 * Get Action link from permission based on the requested action.
 */
export const getActionLink = (permission: Permission, action: string): Link => {
  const {actions} = permission;
  const {link}    = actions.find(({title}) => title === action) || {};
  return link;
};

/**
 * Default Actions for Permissions.
 */
export const defaultActions = {
  Create : false,
  Read   : false,
  Update : false,
  Delete : false,
  Assign : false
};

/**
 * Total Action counts
 */
export const TOTAL_ACTIONS = Object.keys(defaultActions)?.length;

/**
   * Map the Features and Permissions for CTC Admin Role creation.
   * When Feature is getting selected the same time permissions also
   * auto selected based on the feature name matching with permission
   * resource name.
   */
export const featurePermissions = {
  'Device Password' : [Entity.devicepassword]
};

/**
 * Get the unique permissions.
 */
export const getUniquePermissions = (permissions: Permissions) => {
  return permissions?.reduce((unique, attr) => {
    if (!unique.some(({href}) => href === attr.href)) {
      unique.push(attr);
    }
    return unique;
  }, []);
};

/**
 * Get Actions from the list of permissions based on the resource and module name
 * match.
 */
export const getActionLinks = (
  permissionList: GenericObject,
  moduleName: string
) => permissionList?.find(({resource}) => resource === moduleName) || [];

/**
 * Update the permission actions based on the All option
 * select/de-selects.
 */
export const updateAllAction = (permissions: Permissions, checked: boolean): Permissions => {
  return permissions?.actions?.map((item: GenericObject) => {
    item.checked = checked;
    return item;
  });
};

/**
 * Check the permission actions and update the boolean value based on
 * the permission matches.
 */
export const checkActions = (actions: GenericObject, existingPermissions: GenericObject): GenericObject => {
  return actions?.map((item) => {
    const {link} = item;
    const result = existingPermissions?.find(({href}) => href === link?.href);
    item.checked = !!result;
    return item;
  });
};

/**
 * File Permissions related permissions.
 */
export const filePermissionsPermission = [
  Entity.lmuscript,
  Entity.lmufirmware,
  Entity.vbusconfig,
  Entity.vbusfirmware,
  Entity.vbushostedapp,
  Entity.bluetoothfirmware,
  Entity.radiofirmware,
  Entity.custompackagesedgeapp,
  Entity.custompackageshostedapp,
  Entity.custompackagesopkg,
  Entity.customerdatabasedriverid,
  Entity.customerdatabasegeozone,
  Entity.customerdatabaseapn,
  Entity.mdtconfig,
  Entity.mdtfirmware,
  Entity.lmudigitalsignature
];

/**
 * Check user has action for the currently selected action.
 */
export const checkUserHasAccess = (
  actions: GenericObject,
  moduleName: string
): boolean => actions?.some(({title}) => title?.toLowerCase() === moduleName?.toLowerCase());

/**
 * Check user has action for the currently selected action.
 */
export const getPermissionIndex = (
  permissions: GenericObject,
  title: string
): number => permissions?.findIndex(({title : moduleName}) => moduleName?.toLowerCase() === title?.toLowerCase());
